import { api } from './api';

export interface LoginDto {
  email: string;
  password: string;
}

export interface RegisterDto {
  email: string;
  password: string;
  name: string;
  role: 'admin' | 'manager' | 'user';
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  roleDisplayName?: string;
  // What the role allows, as "<area>.<action>" codes
  permissions?: string[];
  username?: string;
  firstName?: string;
  lastName?: string;
  profilePicture?: string | null;
  // Set for a customer or vendor signed in to the portal
  portal?: { kind: 'customer' | 'vendor'; partyId: string; partyName: string } | null;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

// The password was right, but the account has two-step sign-in on: a code is still owed
export interface TwoFactorChallenge {
  twoFactorRequired: true;
  challengeToken: string;
}

export const needsTwoFactor = (answer: AuthResponse | TwoFactorChallenge): answer is TwoFactorChallenge =>
  (answer as TwoFactorChallenge).twoFactorRequired === true;

class AuthService {
  login = async (credentials: LoginDto): Promise<AuthResponse | TwoFactorChallenge> => {
    const response = await api.post<AuthResponse | TwoFactorChallenge>('/auth/login', credentials);
    if (!needsTwoFactor(response.data)) this.setTokens(response.data);
    return response.data;
  }

  // The second step: a code from the authenticator app, or one of the recovery codes
  verifyTwoFactor = async (input: { challengeToken: string; code?: string; recoveryCode?: string }): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/2fa/verify', input);
    this.setTokens(response.data);
    return response.data;
  }

  register = async (data: RegisterDto): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/register', data);
    this.setTokens(response.data);
    return response.data;
  }

  getProfile = async (): Promise<User> => {
    const response = await api.get<User>('/auth/profile');
    return response.data;
  }

  logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // The next person to sign in on this browser gets their own
    localStorage.removeItem('preferences');
  }

  private setTokens = (data: AuthResponse) => {
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
  }

  getCurrentUser = (): User | null => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated = (): boolean => {
    return !!localStorage.getItem('accessToken');
  }
}

export const authService = new AuthService();